import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { Database } from '@/integrations/supabase/types';

type MusicActivity = Database['public']['Tables']['music_activities']['Row'];

export function useMusicActivity() {
  const [activities, setActivities] = useState<MusicActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('music_activities')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setActivities(data as MusicActivity[] || []);
    } catch (error) {
      logger.error('Failed to fetch music activities', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const trackActivity = useCallback(async (
    activityType: MusicActivity['activity_type'],
    trackName: string,
    artistName: string,
    trackBpm?: number,
    runId?: string,
    metadata?: any
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('music_activities')
        .insert({
          user_id: user.id,
          activity_type: activityType,
          track_name: trackName,
          artist_name: artistName,
          track_bpm: trackBpm,
          run_id: runId,
          metadata,
        });

      if (error) throw error;
      logger.info('Music activity tracked', { activityType, trackName });
    } catch (error) {
      logger.error('Failed to track music activity', error);
    }
  }, []);

  // Setup realtime subscription
  useEffect(() => {
    fetchActivities();

    const channel = supabase
      .channel('music_activities_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'music_activities'
        },
        (payload) => {
          setActivities(prev => [payload.new as MusicActivity, ...prev].slice(0, 50));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchActivities]);

  return {
    activities,
    isLoading,
    fetchActivities,
    trackActivity,
  };
}
